import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Tab,
  Tabs,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Alert,
  Avatar,
  // Stack,
  // LinearProgress,
  // Skeleton,
  alpha,
  useTheme,
  CircularProgress,
  Fade
  // useMediaQuery,
} from '@mui/material';
import ComplianceSettings from '../components/ComplianceSettings';
import CustomPHIPatterns from '../components/CustomPHIPatterns';
import {
  Business as BusinessIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Upgrade as UpgradeIcon,
  Security as SecurityIcon
  // TrendingUp as TrendingUpIcon,
  // AdminPanelSettings as AdminIcon,
  // SupervisorAccount as SupervisorIcon,
  // Badge as BadgeIcon,
  // Email as EmailIcon,
  // Domain as DomainIcon,
  // CalendarToday as CalendarIcon,
  // Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  Organization,
  OrganizationMember,
  InviteUserRequest,
  UpdateOrganizationRequest,
  CreateOrganizationRequest
} from '../types/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const OrganizationManagement: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [editOrgDialog, setEditOrgDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [createOrgDialog, setCreateOrgDialog] = useState(false);

  // Form states
  const [orgForm, setOrgForm] = useState<UpdateOrganizationRequest>({});
  const [inviteForm, setInviteForm] = useState<InviteUserRequest>({
    email: '',
    role: 'member'
  });
  const [createOrgForm, setCreateOrgForm] = useState<CreateOrganizationRequest>({
    name: '',
    subscription_plan: 'free'
  });

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);

      // Fetch organization details
      const orgResponse = await axios.get('/api/organizations/me');
      if (orgResponse.data.data) {
        setOrganization(orgResponse.data.data);
        setOrgForm({
          name: orgResponse.data.data.name,
          description: orgResponse.data.data.description,
          domain: orgResponse.data.data.domain
        });

        // Fetch organization members
        const membersResponse = await axios.get('/api/organizations/me/members');
        console.log('Organization members response:', membersResponse.data);
        setMembers(membersResponse.data.data || []);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // User doesn't have an organization
        setOrganization(null);
      } else {
        toast.error('Failed to fetch organization data');
        console.error('Error fetching organization:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async () => {
    try {
      await axios.put('/api/organizations/me', orgForm);
      toast.success('Organization updated successfully');
      setEditOrgDialog(false);
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update organization');
    }
  };

  const handleCreateOrganization = async () => {
    try {
      await axios.post('/api/organizations', createOrgForm);
      toast.success('Organization created successfully');
      setCreateOrgDialog(false);
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create organization');
    }
  };

  const handleInviteUser = async () => {
    try {
      await axios.post('/api/organizations/me/members', inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setInviteDialog(false);
      setInviteForm({ email: '', role: 'member' });
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to invite user');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await axios.delete(`/api/organizations/me/members/${userId}`);
      toast.success('Member removed successfully');
      fetchOrganizationData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove member');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'department_head':
        return 'info';
      case 'team_lead':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getSubscriptionIcon = (plan: string) => {
    switch (plan) {
      case 'enterprise':
        return <StarIcon sx={{ color: 'gold' }} />;
      case 'team':
        return <UpgradeIcon sx={{ color: 'blue' }} />;
      default:
        return <CheckCircleIcon sx={{ color: 'green' }} />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          gap={2}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="body1" color="text.secondary">
            Loading organization data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!organization) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Create Your Organization
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Get started by creating your organization to collaborate with your team.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setCreateOrgDialog(true)}
            >
              Create Organization
            </Button>
          </CardContent>
        </Card>

        {/* Create Organization Dialog */}
        <Dialog
          open={createOrgDialog}
          onClose={() => setCreateOrgDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Organization</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Organization Name"
                value={createOrgForm.name}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={createOrgForm.description || ''}
                onChange={(e) =>
                  setCreateOrgForm({ ...createOrgForm, description: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Domain"
                placeholder="https://example.com"
                value={createOrgForm.domain || ''}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, domain: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                select
                label="Subscription Plan"
                value={createOrgForm.subscription_plan}
                onChange={(e) =>
                  setCreateOrgForm({ ...createOrgForm, subscription_plan: e.target.value as any })
                }
              >
                <MenuItem value="free">Free (5 users, 3 teams)</MenuItem>
                <MenuItem value="team">Team (25 users, 10 teams)</MenuItem>
                <MenuItem value="enterprise">Enterprise (Unlimited)</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOrgDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrganization} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Fade in timeout={600}>
        <Box
          sx={{
            mb: 4,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(30%, -30%)'
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={2.5}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`
              }}
            >
              <BusinessIcon sx={{ fontSize: 32, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  mb: 0.5,
                  background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Organization Management
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <SecurityIcon sx={{ fontSize: 16 }} />
                Manage your organization settings, members, and teams
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>

      <Fade in timeout={800}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {organization.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {organization.description || 'No description'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getSubscriptionIcon(organization.subscription_plan)}
                <Chip
                  label={organization.subscription_plan.toUpperCase()}
                  color={
                    organization.subscription_plan === 'enterprise'
                      ? 'error'
                      : organization.subscription_plan === 'team'
                        ? 'primary'
                        : 'default'
                  }
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  <GroupIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {organization.member_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Members
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.secondary.main, 0.05)
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {organization.team_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Teams
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05)
                  }}
                >
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${organization.user_role?.toUpperCase()}`}
                    color={getRoleColor(organization.user_role || '')}
                    sx={{ fontWeight: 700, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Your Role
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      <Fade in timeout={1000}>
        <Card
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Tabs
              value={currentTab}
              onChange={(_e, newValue) => setCurrentTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.938rem',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                }
              }}
            >
              <Tab label="Overview" />
              <Tab label="Members" />
              <Tab label="Settings" />
              <Tab label="Compliance" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Organization Details
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Name" secondary={organization.name} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Slug" secondary={organization.slug} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Domain" secondary={organization.domain || 'Not set'} />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Created"
                      secondary={new Date(organization.created_at).toLocaleDateString()}
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Features
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Max Users"
                      secondary={organization.features?.max_users || organization.max_users}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Max Teams"
                      secondary={organization.features?.max_teams || organization.max_teams}
                    />
                  </ListItem>
                  {/* <ListItem>
                  <ListItemText primary="Advanced Analytics" />
                  <ListItemSecondaryAction>
                    <Switch 
                      checked={organization.features?.advanced_analytics || false} 
                      disabled
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText primary="Custom Rules" />
                  <ListItemSecondaryAction>
                    <Switch 
                      checked={organization.features?.custom_rules || false} 
                      disabled
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText primary="Priority Support" />
                  <ListItemSecondaryAction>
                    <Switch 
                      checked={organization.features?.priority_support || false} 
                      disabled
                    />
                  </ListItemSecondaryAction>
                </ListItem> */}
                </List>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
            >
              <Typography variant="h6">Organization Members</Typography>
              {(organization.user_role === 'owner' || organization.user_role === 'admin') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setInviteDialog(true)}
                >
                  Invite Member
                </Button>
              )}
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon />
                          <Box>
                            <Typography variant="body2">
                              {member.User?.email || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {member.User?.job_title || 'No title'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.role.replace('_', ' ').toUpperCase()}
                          color={getRoleColor(member.role)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{member.department || 'Not assigned'}</TableCell>
                      <TableCell>{new Date(member.joined_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {(organization.user_role === 'owner' ||
                          organization.user_role === 'admin') &&
                          member.user_id !== user?.id && (
                            <Tooltip title="Remove member">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(member.user_id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
            >
              <Typography variant="h6">Organization Settings</Typography>
              {(organization.user_role === 'owner' || organization.user_role === 'admin') && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditOrgDialog(true)}
                >
                  Edit Settings
                </Button>
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Organization settings can only be modified by owners and administrators.
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Basic Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Name" secondary={organization.name} />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Description"
                          secondary={organization.description || 'Not set'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Domain"
                          secondary={organization.domain || 'Not set'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Subscription
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getSubscriptionIcon(organization.subscription_plan)}
                      <Typography variant="h6">
                        {organization.subscription_plan.charAt(0).toUpperCase() +
                          organization.subscription_plan.slice(1)}{' '}
                        Plan
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Users"
                          secondary={`${organization.member_count}/${organization.max_users === 'unlimited' ? '∞' : organization.max_users}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Teams"
                          secondary={`${organization.team_count}/${organization.max_teams === 'unlimited' ? '∞' : organization.max_teams}`}
                        />
                      </ListItem>
                    </List>
                    {organization.subscription_plan !== 'enterprise' && (
                      <Button variant="outlined" startIcon={<UpgradeIcon />} sx={{ mt: 2 }}>
                        Upgrade Plan
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ComplianceSettings />
              </Grid>
              <Grid item xs={12}>
                <CustomPHIPatterns />
              </Grid>
            </Grid>
          </TabPanel>
        </Card>
      </Fade>

      {/* Edit Organization Dialog */}
      <Dialog open={editOrgDialog} onClose={() => setEditOrgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={orgForm.name || ''}
              onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={orgForm.description || ''}
              onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Domain"
              value={orgForm.domain || ''}
              onChange={(e) => setOrgForm({ ...orgForm, domain: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateOrganization} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="team_lead">Team Lead</MenuItem>
              <MenuItem value="department_head">Department Head</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Department"
              value={inviteForm.department || ''}
              onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>Cancel</Button>
          <Button onClick={handleInviteUser} variant="contained">
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationManagement;
